import { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import SupportsImprimesDevisModal from '../components/SupportsImprimesDevisModal';
import ProductionVideoDevisModal from '../components/ProductionVideoDevisModal';
import CommunicationVisuelleDevisModal from '../components/CommunicationVisuelleDevisModal';
import HabillageSignaletiqueDevisModal from '../components/HabillageSignaletiqueDevisModal';
import CreationEntrepriseDevisModal from '../components/CreationEntrepriseDevisModal';
import FaireContratSheet from '../components/FaireContratSheet';
import AffichesBuilder from '../components/AffichesBuilder';
import { KpiRow, DemandesParService, AvancementProjets, ServiceCard, CustomProjectCard, FilterBar, ProjetsDialog } from '../components/conception/ConceptionDashboard';
import {
  Palette, Monitor, Printer, Video, PaintBucket, Plus, Trash2, ArrowRight, ArrowLeft,
  Check, Briefcase, Layers
} from 'lucide-react';
import { toast } from 'sonner';

const ICONS = {
  'monitor': Monitor,
  'printer': Printer,
  'video': Video,
  'paint-bucket': PaintBucket,
  'briefcase': Briefcase,
};

// Mapping couleur + catégorie filtre + sous-badge pour l'affichage carte
const SERVICE_META = {
  'monitor':      { color: 'bg-gray-700', cat: 'Digital',     subtag: 'Grand format' },
  'printer':      { color: 'bg-gray-700', cat: 'Print',       subtag: 'Flyers et panneaux' },
  'video':        { color: 'bg-gray-700', cat: 'Vidéo',       subtag: 'Vidéos impactantes' },
  'paint-bucket': { color: 'bg-gray-700', cat: 'Signalétique', subtag: 'Décoration' },
  'briefcase':    { color: 'bg-gray-700', cat: 'Digital',     subtag: 'Business et digital' },
};
const FILTERS = ['Tous les services', 'Print', 'Vidéo', 'Digital', 'Signalétique'];

const fmtEUR = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(n || 0);

const DEFAULT_PRESTATIONS = [
  { designation: 'Impression vinyle', dimensions: '', prix_unitaire: 0, quantite: 1 },
  { designation: 'Finition pose',      dimensions: '',  prix_unitaire: 0, quantite: 1 },
  { designation: 'Transport',          dimensions: '',  prix_unitaire: 0, quantite: 1 },
];

export default function Conception({ user, onLogout }) {
  const [services, setServices] = useState([]);
  const [emplacements, setEmplacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState(null);
  const [contratSheetOpen, setContratSheetOpen] = useState(false);
  const [contratService, setContratService] = useState(null);
  const openContratFor = (svc) => { setContratService(svc); setContratSheetOpen(true); };
  const [form, setForm] = useState({
    service_id: '',
    service_titre: '',
    client_nom: '',
    client_email: '',
    modele: '',
    largeur_cm: '',
    hauteur_cm: '',
    prix_unitaire: '',
    quantite: 1,
    remise_pct: 0,
    prestations: [...DEFAULT_PRESTATIONS],
    tva_pct: 20,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [supportsImprimeOpen, setSupportsImprimeOpen] = useState(false);
  const [productionVideoOpen, setProductionVideoOpen] = useState(false);
  const [communicationVisuelleOpen, setCommunicationVisuelleOpen] = useState(false);
  const [habillageOpen, setHabillageOpen] = useState(false);
  const [creationEntrepriseOpen, setCreationEntrepriseOpen] = useState(false);
  const [affichesBuilderOpen, setAffichesBuilderOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('Tous les services');
  const [serviceSearch, setServiceSearch] = useState('');
  const [stats, setStats] = useState(null);
  const [devisList, setDevisList] = useState([]);
  const [projetsOpen, setProjetsOpen] = useState(false);

  const loadStats = async () => {
    try {
      const [st, dv] = await Promise.all([axios.get('/conception/stats'), axios.get('/conception/devis')]);
      setStats(st.data);
      setDevisList(dv.data || []);
    } catch { setStats(null); }
  };
  useEffect(() => { loadStats(); }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('ouvrir') === 'affiches') {
      setAffichesBuilderOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    (async () => {
      try {
        const [sRes, eRes] = await Promise.all([
          axios.get('/conception/services'),
          axios.get('/emplacements').catch(() => ({ data: [] })),
        ]);
        setServices(sRes.data);
        setEmplacements(eRes.data || []);
      } catch (e) {
        console.error(e);
        toast.error('Erreur lors du chargement des services');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openDevisFor = (svc) => {
    // Workflow spécial pour Communication visuelle
    if (svc.id === 'communication-visuelle') {
      setCommunicationVisuelleOpen(true);
      return;
    }
    // Workflow spécial pour Supports imprimés (interface dédiée avec prospects + transport)
    if (svc.id === 'supports-imprimes') {
      setSupportsImprimeOpen(true);
      return;
    }
    // Workflow spécial pour Production vidéo
    if (svc.id === 'production-video') {
      setProductionVideoOpen(true);
      return;
    }
    // Workflow spécial pour Habillage & Signalétique
    if (svc.id === 'habillage-signaletique') {
      setHabillageOpen(true);
      return;
    }
    // Workflow spécial pour Création d'entreprise
    if (svc.id === 'creation-entreprise') {
      setCreationEntrepriseOpen(true);
      return;
    }
    setSelectedService(svc);
    setStep(1);
    setForm({
      service_id: svc.id,
      service_titre: svc.titre,
      client_nom: '',
      client_email: '',
      modele: svc.titre,
      largeur_cm: 400,
      hauteur_cm: 300,
      prix_unitaire: 1500,
      quantite: 1,
      remise_pct: 10,
      prestations: DEFAULT_PRESTATIONS.map((p) => ({ ...p })),
      tva_pct: 20,
      notes: '',
    });
    setOpen(true);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updatePresta = (i, k, v) => {
    setForm((f) => {
      const arr = [...f.prestations];
      arr[i] = { ...arr[i], [k]: v };
      return { ...f, prestations: arr };
    });
  };
  const addPresta = () => setField('prestations', [...form.prestations, { designation: '', dimensions: '', prix_unitaire: 0, quantite: 1 }]);
  const removePresta = (i) => setField('prestations', form.prestations.filter((_, j) => j !== i));

  // Calculs live pour le récapitulatif
  const sousTotalPrestations = (form.prestations || []).reduce(
    (s, p) => s + (parseFloat(p.prix_unitaire) || 0) * (parseInt(p.quantite) || 0),
    0
  );
  const remiseMontant = sousTotalPrestations * (parseFloat(form.remise_pct || 0) / 100);
  const totalHT = sousTotalPrestations - remiseMontant;
  const tvaMontant = totalHT * (parseFloat(form.tva_pct || 0) / 100);
  const totalTTC = totalHT + tvaMontant;

  const totalEstimeStep1 = (parseFloat(form.prix_unitaire) || 0) * (parseInt(form.quantite) || 0) * (1 - (parseFloat(form.remise_pct || 0) / 100));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        largeur_cm: parseFloat(form.largeur_cm) || 0,
        hauteur_cm: parseFloat(form.hauteur_cm) || 0,
        prix_unitaire: parseFloat(form.prix_unitaire) || 0,
        quantite: parseInt(form.quantite) || 1,
        remise_pct: parseFloat(form.remise_pct) || 0,
        tva_pct: parseFloat(form.tva_pct) || 0,
        prestations: (form.prestations || []).map((p) => ({
          designation: p.designation,
          dimensions: p.dimensions || '',
          prix_unitaire: parseFloat(p.prix_unitaire) || 0,
          quantite: parseInt(p.quantite) || 1,
        })),
      };
      const r = await axios.post('/conception/devis', payload);
      toast.success(`Devis ${r.data.numero} créé avec succès`);
      loadStats();
      setOpen(false);
      setStep(1);
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors de la création du devis');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout} currentPage="conception">
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-14 w-14 border-b-4 border-orange-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout} currentPage="conception">
      <div className="space-y-6" data-testid="conception-root">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-lg shadow-orange-200">
              <Palette className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-extrabold text-[#0f1a3a] tracking-tight">Conception</h1>
              <p className="text-slate-500 text-sm md:text-base">Donnez vie à vos idées avec des créations qui marquent.</p>
            </div>
          </div>
          <Button
            onClick={() => setAffichesBuilderOpen(true)}
            className="bg-blue-700 hover:bg-blue-800 text-white gap-2 h-11 px-5 rounded-xl font-bold shadow-sm"
            data-testid="btn-creation-affiches"
          >
            <Layers className="h-4 w-4" /> Création des affiches
          </Button>
        </div>

        {stats && <KpiRow stats={stats} />}

        {stats && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <DemandesParService stats={stats} services={services} />
            <AvancementProjets stats={stats} onManage={() => setProjetsOpen(true)} />
          </div>
        )}

        <FilterBar filters={FILTERS} active={activeFilter} onChange={setActiveFilter} search={serviceSearch} onSearch={setServiceSearch} />

        {/* Grille des services */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {services
            .filter((svc) => {
              if (serviceSearch && !svc.titre.toLowerCase().includes(serviceSearch.toLowerCase())) return false;
              if (activeFilter === 'Tous les services') return true;
              return (SERVICE_META[svc.icon] || {}).cat === activeFilter;
            })
            .map((svc, i) => (
              <ServiceCard
                key={svc.id}
                svc={svc}
                delay={i * 70}
                st={(stats?.services || []).find((x) => x.id === svc.id)}
                subtag={(SERVICE_META[svc.icon] || {}).subtag || svc.badge}
                Icon={ICONS[svc.icon] || Monitor}
                onDevis={() => openDevisFor(svc)}
                onContrat={() => openContratFor(svc)}
              />
            ))}
          <CustomProjectCard />
        </div>
      </div>

      <ProjetsDialog open={projetsOpen} onOpenChange={setProjetsOpen} devis={devisList} onChanged={loadStats} />

      {/* Side sheet : Nouveau devis 3-step */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="sm:max-w-xl w-full overflow-y-auto p-0" data-testid="devis-sheet">
          <SheetHeader className="p-5 border-b sticky top-0 bg-white z-10">
            <SheetTitle className="text-xl">
              {step === 1 ? `Nouveau devis – ${form.service_titre || ''}` :
               step === 2 ? `Détails du devis – ${form.service_titre || ''}` :
                            `Récapitulatif – ${form.service_titre || ''}`}
            </SheetTitle>
            {/* Stepper */}
            <div className="flex items-center justify-between pt-2">
              {[1, 2, 3].map((n) => (
                <div key={n} className="flex-1 flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step === n ? 'bg-orange-500 text-white' :
                    step > n ? 'bg-emerald-500 text-white' :
                    'bg-gray-200 text-gray-500'
                  }`}>
                    {step > n ? <Check className="h-4 w-4" /> : n}
                  </div>
                  <span className={`text-xs font-medium ${step === n ? 'text-orange-600' : step > n ? 'text-emerald-600' : 'text-gray-400'}`}>
                    {n === 1 ? 'Informations' : n === 2 ? 'Détails' : 'Récapitulatif'}
                  </span>
                  {n < 3 && <div className={`flex-1 h-0.5 ${step > n ? 'bg-emerald-500' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>
          </SheetHeader>

          <div className="p-5 space-y-4">
            {/* ETAPE 1 */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <Label>Modèle de production</Label>
                  <Select value={form.modele} onValueChange={(v) => setField('modele', v)}>
                    <SelectTrigger data-testid="input-modele"><SelectValue placeholder="Choisir un modèle" /></SelectTrigger>
                    <SelectContent>
                      {services.map((s) => <SelectItem key={s.id} value={s.titre}>{s.titre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Dimensions</Label>
                  <div className="grid grid-cols-2 gap-3 mt-1">
                    <div>
                      <span className="text-xs text-gray-500">Largeur (cm)</span>
                      <Input type="number" value={form.largeur_cm} onChange={(e) => setField('largeur_cm', e.target.value)} data-testid="input-largeur" />
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Hauteur (cm)</span>
                      <Input type="number" value={form.hauteur_cm} onChange={(e) => setField('hauteur_cm', e.target.value)} data-testid="input-hauteur" />
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Prix unitaire (€)</Label>
                  <Input type="number" value={form.prix_unitaire} onChange={(e) => setField('prix_unitaire', e.target.value)} data-testid="input-prix" />
                </div>

                <div>
                  <Label>Quantité</Label>
                  <Input type="number" value={form.quantite} onChange={(e) => setField('quantite', e.target.value)} data-testid="input-quantite" />
                </div>

                <div>
                  <Label>Remise (%) <span className="text-xs text-gray-500 font-normal">(optionnel)</span></Label>
                  <Input type="number" value={form.remise_pct} onChange={(e) => setField('remise_pct', e.target.value)} />
                </div>

                <div>
                  <Label>Client</Label>
                  <Input placeholder="Nom du client" value={form.client_nom} onChange={(e) => setField('client_nom', e.target.value)} />
                </div>

                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center justify-between">
                  <span className="text-sm text-gray-700 font-medium">Total estimé</span>
                  <span className="text-xl font-bold text-blue-700">{fmtEUR(totalEstimeStep1)}</span>
                </div>
              </div>
            )}

            {/* ETAPE 2 */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Paramètres */}
                  <div className="space-y-3 bg-gray-50 rounded-xl p-4">
                    <h4 className="font-semibold text-gray-800">Paramètres</h4>
                    <div>
                      <Label className="text-xs">Modèle</Label>
                      <Input value={form.modele} onChange={(e) => setField('modele', e.target.value)} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Largeur (cm)</Label>
                        <Input type="number" value={form.largeur_cm} onChange={(e) => setField('largeur_cm', e.target.value)} />
                      </div>
                      <div>
                        <Label className="text-xs">Hauteur (cm)</Label>
                        <Input type="number" value={form.hauteur_cm} onChange={(e) => setField('hauteur_cm', e.target.value)} />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Prix unitaire (€)</Label>
                      <Input type="number" value={form.prix_unitaire} onChange={(e) => setField('prix_unitaire', e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Quantité</Label>
                      <Input type="number" value={form.quantite} onChange={(e) => setField('quantite', e.target.value)} />
                    </div>
                    <div>
                      <Label className="text-xs">Remise (%) <span className="text-gray-500">(optionnel)</span></Label>
                      <Input type="number" value={form.remise_pct} onChange={(e) => setField('remise_pct', e.target.value)} />
                    </div>
                  </div>

                  {/* Aperçu des prestations */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-gray-800">Aperçu des prestations</h4>
                      <Button size="sm" variant="outline" onClick={addPresta}>
                        <Plus className="h-3.5 w-3.5 mr-1" /> Ajouter
                      </Button>
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="text-left px-2 py-2">Désignation</th>
                            <th className="text-left px-2 py-2 w-24">Dimensions</th>
                            <th className="text-right px-2 py-2 w-20">PU (€)</th>
                            <th className="text-right px-2 py-2 w-14">Qté</th>
                            <th className="text-right px-2 py-2 w-24">Total</th>
                            <th className="w-8"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.prestations.map((p, i) => (
                            <tr key={i} className="border-t">
                              <td className="p-1"><Input className="h-8 text-xs" value={p.designation} onChange={(e) => updatePresta(i, 'designation', e.target.value)} /></td>
                              <td className="p-1"><Input className="h-8 text-xs" value={p.dimensions} onChange={(e) => updatePresta(i, 'dimensions', e.target.value)} /></td>
                              <td className="p-1"><Input type="number" className="h-8 text-xs text-right" value={p.prix_unitaire} onChange={(e) => updatePresta(i, 'prix_unitaire', e.target.value)} /></td>
                              <td className="p-1"><Input type="number" className="h-8 text-xs text-right" value={p.quantite} onChange={(e) => updatePresta(i, 'quantite', e.target.value)} /></td>
                              <td className="p-1 text-right font-medium">{fmtEUR((parseFloat(p.prix_unitaire)||0) * (parseInt(p.quantite)||0))}</td>
                              <td className="p-1 text-center">
                                <button onClick={() => removePresta(i)} className="text-red-500 hover:text-red-700">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <div className="space-y-1 mt-3 text-sm">
                      <div className="flex justify-between"><span className="text-gray-600">Sous-total</span><span className="font-medium">{fmtEUR(sousTotalPrestations)}</span></div>
                      <div className="flex justify-between text-red-600"><span>Remise ({form.remise_pct || 0}%)</span><span>-{fmtEUR(remiseMontant)}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">Total HT</span><span className="font-medium">{fmtEUR(totalHT)}</span></div>
                      <div className="flex justify-between"><span className="text-gray-600">TVA ({form.tva_pct}%)</span><span className="font-medium">{fmtEUR(tvaMontant)}</span></div>
                      <div className="flex justify-between text-base pt-2 border-t"><span className="font-bold">Total TTC</span><span className="font-bold text-blue-700">{fmtEUR(totalTTC)}</span></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ETAPE 3 */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <h4 className="font-bold text-emerald-800 mb-2">Récapitulatif du devis</h4>
                  <div className="text-sm text-gray-700 space-y-1">
                    <div><b>Service :</b> {form.service_titre}</div>
                    <div><b>Client :</b> {form.client_nom || '—'}</div>
                    <div><b>Modèle :</b> {form.modele}</div>
                    <div><b>Dimensions :</b> {form.largeur_cm} × {form.hauteur_cm} cm</div>
                  </div>
                </div>

                <div>
                  <Label>Notes / Conditions</Label>
                  <Textarea rows={3} value={form.notes} onChange={(e) => setField('notes', e.target.value)} placeholder="Ex. Délai de production 15 jours, paiement 50% à la commande…" />
                </div>

                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100 text-xs uppercase">
                      <tr>
                        <th className="text-left px-3 py-2">Désignation</th>
                        <th className="text-left px-3 py-2">Dim.</th>
                        <th className="text-right px-3 py-2">PU</th>
                        <th className="text-right px-3 py-2">Qté</th>
                        <th className="text-right px-3 py-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.prestations.map((p, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-3 py-2">{p.designation}</td>
                          <td className="px-3 py-2">{p.dimensions || '—'}</td>
                          <td className="px-3 py-2 text-right">{fmtEUR(parseFloat(p.prix_unitaire) || 0)}</td>
                          <td className="px-3 py-2 text-right">{p.quantite}</td>
                          <td className="px-3 py-2 text-right font-medium">{fmtEUR((parseFloat(p.prix_unitaire)||0) * (parseInt(p.quantite)||0))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="bg-gray-50 rounded-xl p-4 space-y-1 text-sm">
                  <div className="flex justify-between"><span>Sous-total</span><span>{fmtEUR(sousTotalPrestations)}</span></div>
                  <div className="flex justify-between text-red-600"><span>Remise ({form.remise_pct || 0}%)</span><span>-{fmtEUR(remiseMontant)}</span></div>
                  <div className="flex justify-between font-semibold"><span>Total HT</span><span>{fmtEUR(totalHT)}</span></div>
                  <div className="flex justify-between"><span>TVA ({form.tva_pct}%)</span><span>{fmtEUR(tvaMontant)}</span></div>
                  <div className="flex justify-between text-lg pt-2 border-t border-gray-300"><span className="font-bold">Total TTC</span><span className="font-bold text-blue-700">{fmtEUR(totalTTC)}</span></div>
                </div>
              </div>
            )}
          </div>

          {/* Footer nav */}
          <div className="p-5 border-t bg-white flex items-center justify-between sticky bottom-0">
            {step > 1 ? (
              <Button variant="outline" onClick={() => setStep(step - 1)}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Retour
              </Button>
            ) : <div />}
            {step < 3 ? (
              <Button className="bg-orange-500 hover:bg-orange-600 text-white" onClick={() => setStep(step + 1)} data-testid="btn-next-step">
                Étape suivante <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            ) : (
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submit} disabled={saving} data-testid="btn-submit-devis">
                <Check className="h-4 w-4 mr-1" /> {saving ? 'Enregistrement…' : 'Valider le devis'}
              </Button>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal dédié Supports imprimés */}
      <SupportsImprimesDevisModal
        open={supportsImprimeOpen}
        onOpenChange={setSupportsImprimeOpen}
        onSaved={loadStats}
      />

      {/* Modal dédié Production vidéo */}
      <ProductionVideoDevisModal
        open={productionVideoOpen}
        onOpenChange={setProductionVideoOpen}
        onSaved={loadStats}
      />

      {/* Modal dédié Communication visuelle */}
      <CommunicationVisuelleDevisModal
        open={communicationVisuelleOpen}
        onOpenChange={setCommunicationVisuelleOpen}
        onSaved={loadStats}
      />

      {/* Modal dédié Habillage & Signalétique */}
      <HabillageSignaletiqueDevisModal
        open={habillageOpen}
        onOpenChange={setHabillageOpen}
        onSaved={loadStats}
      />

      {/* Modal dédié Création d'entreprise */}
      <CreationEntrepriseDevisModal
        open={creationEntrepriseOpen}
        onOpenChange={setCreationEntrepriseOpen}
        onSaved={loadStats}
      />

      {/* Sheet Faire un contrat */}
      <FaireContratSheet
        open={contratSheetOpen}
        onOpenChange={setContratSheetOpen}
        service={contratService}
      />

      {/* Création des affiches (déplacé depuis Emplacement) */}
      <AffichesBuilder
        open={affichesBuilderOpen}
        onClose={() => setAffichesBuilderOpen(false)}
        emplacements={emplacements}
      />
    </Layout>
  );
}
